// Заглушка поля 3x3: две медные створки, которые закрывают барабаны и разъезжаются.
//
// Игра начинается с открытия: створки стоят закрытыми и уезжают, показывая барабаны. Зовёт
// его игровое поле, когда машина подъехала, см. ui/arrival.ts, — сама заглушка не решает,
// когда игре начаться.
//
// Дальше заглушка объявляет фичу — захлопывается, показывает её имя и снова открывается.
// Она же уводит взгляд с поля: бонус Steam Spin играет на мини-слоте выше, и всё это время
// поле стоит закрытым, см. GDD, раздел 5.3.
//
// Закрытая заглушка — это ещё и место, где игра говорит с игроком. На верхней створке стоит
// надпись, под ней набегает счётчик выигрыша, а на нижней створке всплывает кнопка. Отдельного
// окна поверх поля игре не нужно: всё, что раньше показывал попап завершения бонуса, помещается
// на самих створках, см. GDD, раздел 7.2, попап id_4.
//
// На тех же створках игра называет крупный выигрыш: створки сходятся, под надписью BIG WIN
// набегает сумма и стоит пару секунд, см. GDD, раздел 3.7. Отдельного попапа Big Win у игры
// нет по той же причине: закрытое поле уже держит и надпись, и счётчик.
//
// Надпись и кнопка стоят на слотах, где художник оставил под них заготовки. Слоты висят
// на костях створок, поэтому Spine ведёт их за створками, и своё место они держат сами,
// без единой координаты в игре, см. REEL_COVER_CAPTION и REEL_COVER_BUTTON.
//
// Прятать их за краем поля игре тоже не надо. В скелете лежит обрезка по рамке поля,
// и Spine вешает её маской на узлы слотов. Поэтому надпись въезжает вместе со створкой
// из-под рамки и так же уезжает под неё: за полем её попросту нет.

import { Container, Text, type TextStyleOptions } from 'pixi.js';

import type { Cents } from '../api/gameConfig';
import { MISC_UI_SHEET, REEL_COVER_BUTTON, REEL_COVER_CAPTION, REEL_COVER_SKELETON, TITLE_FONT } from '../assets/catalog';
import type { AssetManager, Spine } from '../assets/manager';
import { textFamily } from '../i18n/text';
import type { SoundVoice } from '../sound/engine';
import { sfx, type SfxName } from '../sound/sound';

import { AmountLabel, countedAmount } from './amount';
import { fitLabel } from './label';
import { PlateButton } from './plateButton';
import { animationSeconds, holdSpine, placeSpine } from './scene';

/** Сколько надпись стоит на закрытом поле. Створки едут своё время, оно задано анимацией. */
const CAPTION_SECONDS: number = 0.5;

/** Высота кегля надписи в координатах скелета. Взята с запасом: надпись ужимается под створку. */
const CAPTION_SIZE: number = 110;

/** Насколько широкой может быть надпись: ширина створки за вычетом её рамки. */
const CAPTION_WIDTH: number = 560;

/**
 * Насколько высокой может быть надпись.
 *
 * Надпись стоит серединой на заготовке художника, а сверху её режет рамка поля. От заготовки
 * до рамки 148 пикселей картинки барабанов, поэтому в обе стороны от неё помещается 296.
 * Поздравление о бонусе идёт тремя строками и упирается как раз в этот предел.
 */
const CAPTION_HEIGHT: number = 290;

/** Высота цифры счётчика в координатах скелета. */
const AMOUNT_HEIGHT: number = 110;

/**
 * Насколько счётчик стоит ниже надписи.
 *
 * Надпись висит на заготовке художника в верхней части поля, а счётчик игрок читает в его
 * середине, как на референсе в GDD, раздел 7.2. От заготовки до середины поля 107 пикселей
 * картинки барабанов: кость верхней створки лежит на 52 выше начала скелета, заготовка —
 * ещё на 37 выше него, а само начало скелета стоит на 18 выше середины поля.
 */
const AMOUNT_DROP: number = 107.6;

/** За сколько счётчик добирается от нуля до всей суммы. */
const COUNT_SECONDS: number = 1.2;

/** Сколько вся сумма стоит на виду после счёта. */
const COUNT_HOLD_SECONDS: number = 0.4;

/**
 * Сколько вся сумма Big Win стоит на закрытом поле после счёта.
 *
 * GDD, раздел 3.7, даёт на это 2-3 секунды: игрок должен успеть оценить размер выигрыша.
 * Взята нижняя граница — крупный выигрыш выпадает не раз за сессию, и держать его дольше
 * значит держать и игрока.
 */
const BIG_WIN_HOLD_SECONDS: number = 2;

/** Сколько поле стоит закрытым под Big Win: счёт суммы и время на то, чтобы её прочитать. */
const BIG_WIN_SECONDS: number = COUNT_SECONDS + BIG_WIN_HOLD_SECONDS;

/**
 * За сколько уходит звук хода створок, когда сам ход прошли разом.
 *
 * Створки прыгают на своё место — игрок торопит показ. Звук так прыгнуть не может:
 * оборванный на полуслове, он слышен щелчком, см. ADR-50.
 */
const HUSH_SECONDS: number = 0.15;

/**
 * Сколько кнопка на створке ждёт игрока.
 *
 * Кнопка игру не держит: она торопит показ, а не разрешает его. Не дождавшись игрока,
 * она жмётся сама, и бонус идёт дальше без единого нажатия.
 */
const BUTTON_SECONDS: number = 2;

/** Высота кегля надписи на кнопке. */
const LABEL_SIZE: number = 64;

/** Стиль надписи: светлые буквы с тёмной обводкой, как на референсе попапа в GDD, раздел 7.2. */
function captionStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: CAPTION_SIZE,
    fontWeight: 'bold',
    fill: 0xfff4dd,
    stroke: { color: 0x40210f, width: CAPTION_SIZE / 9, join: 'round' },
    align: 'center',
    letterSpacing: 4,
  };
}

/** Стиль надписи на кнопке: тёмная медь на светлой плашке, как на референсе в GDD, раздел 7.2. */
function labelStyle(family: string): TextStyleOptions {
  return {
    fontFamily: textFamily(family),
    fontSize: LABEL_SIZE,
    fontWeight: 'bold',
    fill: 0x8c3a17,
    letterSpacing: 6,
  };
}

/**
 * Что делает заглушка.
 *
 * open — поле открыто, показывать нечего.
 * closing — створки едут навстречу друг другу.
 * closed — поле закрыто, надпись на виду.
 * opening — створки разъезжаются.
 */
type CoverPhase = 'open' | 'closing' | 'closed' | 'opening';

/** Идущий счёт суммы: до чего считать, сколько уже отсчитано и будит ли его конец ждущего. */
interface Counting {
  readonly total: Cents;

  /** Ждут ли конца счёта. Big Win ждёт открытия створок, а счётчик доходит до суммы под ними. */
  readonly wakes: boolean;

  passed: number;
}

/** Заглушка поля: закрывает барабаны, объявляет фичу и говорит с игроком на створках. */
export class FieldCover {
  readonly view: Spine;

  readonly #caption: Text;
  readonly #amount: AmountLabel;
  readonly #button: PlateButton;

  /** Игра начинается с закрытого поля: створки уезжают, когда машина подъехала. */
  #phase: CoverPhase = 'closed';
  #seconds = 0;
  #onFinished: (() => void) | null = null;

  /** Остаётся ли поле закрытым после того, как створки сошлись. Первое открытие ждёт release. */
  #held = true;

  /** Сколько поле стоит закрытым, прежде чем створки поедут обратно. */
  #closedSeconds: number = CAPTION_SECONDS;

  /** Идущий счёт суммы. Null — счётчик не идёт. */
  #counting: Counting | null = null;

  /** Сколько кнопке осталось ждать игрока. Ноль — кнопка не ждёт. */
  #waiting = 0;

  /** Идущий звук хода створок. Null — створки стоят. */
  #blinds: SoundVoice | null = null;

  /**
   * @param x Середина поля по горизонтали в координатах картинки барабанов.
   * @param y Середина поля по вертикали там же.
   */
  constructor(assets: AssetManager, x: number, y: number) {
    this.view = assets.spine(REEL_COVER_SKELETON);
    placeSpine(this.view, 1, x, y);

    this.#caption = new Text({ text: '', style: captionStyle(assets.font(TITLE_FONT)) });
    this.#caption.anchor.set(0.5);

    this.#amount = new AmountLabel(assets, AMOUNT_HEIGHT);
    this.#amount.view.visible = false;

    const plate = assets.sheet(MISC_UI_SHEET);
    const faces = { idle: plate.plateIdle, hover: plate.plateHover, active: plate.plateActive };

    this.#button = new PlateButton(faces, labelStyle(assets.font(TITLE_FONT)), () => this.#pressed());
    this.#button.view.visible = false;

    // Узлы слотов Spine перезадаёт целиком на каждом кадре, поэтому своё место надпись,
    // счётчик и кнопка держат внутри узла, а не на нём.
    //
    // Ось Y у Spine смотрит вверх, у Pixi вниз, поэтому смещение заготовки идёт с минусом.
    this.#caption.y = -REEL_COVER_CAPTION.offsetY;
    this.#amount.view.y = -REEL_COVER_CAPTION.offsetY + AMOUNT_DROP;
    this.#button.view.y = -REEL_COVER_BUTTON.offsetY;

    this.view.addSlotObject(REEL_COVER_CAPTION.slot, holder(this.#caption, this.#amount.view));
    this.view.addSlotObject(REEL_COVER_BUTTON.slot, holder(this.#button.view));

    // Створки стоят закрытыми и ждут release: поле открывается, когда машина подъехала,
    // см. ui/arrival.ts. Сама заглушка не решает, когда игре начаться.
    holdSpine(this.view, REEL_COVER_SKELETON.animations.close);
  }

  /**
   * Закрывает поле, показывает надпись и снова открывает его.
   *
   * Новое объявление обрывает прежнее: держать два разом заглушка не умеет, а брошенное
   * ожидание не кончилось бы никогда.
   *
   * @param text Имя фичи. Оно идёт на створку как есть, поэтому регистр задаёт зовущий.
   */
  announce(text: string): Promise<void> {
    return this.#close(text, false);
  }

  /**
   * Закрывает поле надписью и оставляет его закрытым.
   *
   * Так игра уводит взгляд с поля на другое место экрана: бонус Steam Spin играет на мини-слоте
   * выше, и основное поле стоит закрытым всё это время, см. GDD, раздел 5.3.
   *
   * Ожидание кончается, когда створки сошлись. Открывает поле обратно release. Скип проходит
   * закрытие, но закрытым поле оставляет: открывать его посреди бонуса нечего.
   */
  hold(text: string): Promise<void> {
    return this.#close(text, true);
  }

  /**
   * Закрывает поле, гонит по нему счётчик под надписью и снова открывает его.
   *
   * Так игра называет крупный выигрыш: обычный счётчик в середине поля для него мал,
   * см. GDD, раздел 3.7. Ожидание кончается вместе с открытием, а не со счётом: сумма
   * стоит на створках ещё пару секунд, чтобы игрок успел её прочитать.
   *
   * Скип проходит показ разом: счётчик доходит до всей суммы, и створки едут открываться.
   *
   * @param text Надпись над суммой. Она идёт как есть, поэтому регистр задаёт зовущий.
   */
  bigWin(text: string, total: Cents): Promise<void> {
    const shown: Promise<void> = this.#close(text, false, BIG_WIN_SECONDS);

    // Счётчик заводится после закрытия: оно сняло со створок всё, что на них стояло.
    this.#startCount(total, false);

    return shown;
  }

  /** Меняет надпись на уже закрытом поле и убирает с него счётчик. */
  say(text: string): void {
    this.#counting = null;
    this.#amount.view.visible = false;
    this.#setCaption(text);
  }

  /**
   * Гонит счётчик по закрытому полю и ждёт, пока он дойдёт до всей суммы.
   *
   * Так игра называет выигрыш за бонус: сумма набегает там же, где до неё стояла надпись,
   * см. GDD, раздел 7.2, попап id_4. Надпись на это время уходит: читают сейчас число.
   */
  count(total: Cents): Promise<void> {
    this.#wake();

    this.#caption.text = '';
    this.#startCount(total, true);

    return new Promise<void>((resolve) => {
      this.#onFinished = resolve;
    });
  }

  /**
   * Показывает кнопку на нижней створке и ждёт, пока её нажмут.
   *
   * Кнопка игру не держит: под ней нет ни затемнения, ни блокировки, и кнопка спина всё это
   * время живая. Она торопит показ, поэтому не дождавшись игрока, жмётся сама.
   *
   * @param label Надпись на кнопке. Она идёт как есть, поэтому регистр задаёт зовущий.
   * @param seconds Сколько кнопка ждёт игрока. По умолчанию столько же, сколько всякая другая.
   */
  press(label: string, seconds: number = BUTTON_SECONDS): Promise<void> {
    this.#wake();

    this.#button.label = label;
    this.#button.show();
    this.#waiting = seconds;

    return new Promise<void>((resolve) => {
      this.#onFinished = resolve;
    });
  }

  /** Открывает поле, закрытое hold, и ждёт, пока створки разъедутся. */
  release(): Promise<void> {
    this.#held = false;
    this.#clearFace();

    if (this.#phase === 'open')
      return Promise.resolve();

    // Прежнее ожидание обрывается: держать два разом заглушка не умеет, а брошенное
    // ожидание не кончилось бы никогда.
    this.#wake();
    this.#open();

    return new Promise<void>((resolve) => {
      this.#onFinished = resolve;
    });
  }

  /**
   * Проходит показ разом: створки едут открываться, не досматривая надпись.
   *
   * Счётчик доходит до всей суммы одним махом, а кнопка уходит без нажатия: игрок торопит
   * показ, и ждать его она больше не должна.
   */
  skip(): void {
    if (this.#phase === 'open' || this.#phase === 'opening')
      return;

    if (this.#counting !== null) {
      this.#amount.draw(this.#counting.total);
      this.#counting = null;
    }

    this.#waiting = 0;
    this.#button.hide();

    // Створки прошли свой ход разом, и звук уходит с ними: игрок его уже не слушает.
    this.#hush(HUSH_SECONDS);

    // Закрытое под бонус поле скип открывать не должен: игрок торопит показ, а не уходит с него.
    if (this.#held) {
      this.#stayClosed();
      return;
    }

    this.#open();
  }

  update(seconds: number): void {
    this.#button.update(seconds);
    this.#advanceCount(seconds);
    this.#advanceButton(seconds);

    if (this.#phase === 'open')
      return;

    this.#seconds += seconds;

    if (this.#seconds < this.#length)
      return;

    if (this.#phase === 'closing') {
      this.#stayClosed();
      return;
    }

    if (this.#phase === 'closed') {
      // Поле закрыто под бонус: открывать его нечего, ждущего закрытия уже разбудили.
      if (!this.#held)
        this.#open();

      return;
    }

    this.#phase = 'open';
    this.#seconds = 0;
    this.#wake();
  }

  /** Сколько длится нынешняя часть объявления. */
  get #length(): number {
    if (this.#phase === 'closed')
      return this.#closedSeconds;

    const { open, close } = REEL_COVER_SKELETON.animations;

    return animationSeconds(this.view, this.#phase === 'closing' ? close : open);
  }

  /** Ставит на створки счётчик и пускает его с нуля. */
  #startCount(total: Cents, wakes: boolean): void {
    this.#amount.clear();
    this.#amount.draw(0);
    this.#amount.view.visible = true;
    this.#counting = { total, wakes, passed: 0 };
  }

  /**
   * Гонит счётчик и будит того, кто ждёт его конца.
   *
   * Идёт счёт только по закрытому полю: створки везут сумму под рамкой, и счёт под ними
   * прошёл бы у игрока за спиной.
   */
  #advanceCount(seconds: number): void {
    const counting: Counting | null = this.#counting;
    if (counting === null || this.#phase !== 'closed')
      return;

    counting.passed += seconds;
    this.#amount.draw(countedAmount(counting.total, counting.passed, COUNT_SECONDS));

    if (counting.passed < COUNT_SECONDS + COUNT_HOLD_SECONDS)
      return;

    this.#counting = null;

    // Big Win считает под закрытыми створками и ждёт их, а не счётчика: разбуди его здесь,
    // и показ пошёл бы дальше, пока сумма ещё стоит на поле.
    if (counting.wakes)
      this.#wake();
  }

  /** Ведёт срок кнопки. Не дождавшись игрока, кнопка жмётся сама. */
  #advanceButton(seconds: number): void {
    if (this.#waiting <= 0)
      return;

    this.#waiting -= seconds;
    if (this.#waiting > 0)
      return;

    this.#waiting = 0;
    this.#button.press();
  }

  /** Кнопку нажали. Ждать больше нечего, показ идёт дальше. */
  #pressed(): void {
    this.#waiting = 0;
    this.#wake();
  }

  /**
   * Пускает створки навстречу друг другу с заданной надписью.
   *
   * @param held Оставить ли поле закрытым. Объявление открывает его само, бонус — по release.
   * @param closed Сколько поле стоит закрытым. Имя фичи читается быстро, сумма Big Win — нет.
   */
  #close(text: string, held: boolean, closed: number = CAPTION_SECONDS): Promise<void> {
    this.#wake();
    this.#clearFace();
    this.#setCaption(text);

    this.#phase = 'closing';
    this.#held = held;
    this.#closedSeconds = closed;
    this.#seconds = 0;
    this.view.state.setAnimation(0, REEL_COVER_SKELETON.animations.close, false);
    this.#play('blindsClose');

    return new Promise<void>((resolve) => {
      this.#onFinished = resolve;
    });
  }

  /**
   * Ставит поле закрытым.
   *
   * Закрытие кончено, поэтому тот, кто ждал именно его, будится здесь: бонус играет
   * под закрытым полем и открытия не дождался бы. Объявление ждёт открытия и спит дальше.
   */
  #stayClosed(): void {
    holdSpine(this.view, REEL_COVER_SKELETON.animations.close);

    this.#phase = 'closed';
    this.#seconds = 0;

    if (this.#held)
      this.#wake();
  }

  #open(): void {
    this.#phase = 'opening';
    this.#seconds = 0;
    this.view.state.setAnimation(0, REEL_COVER_SKELETON.animations.open, false);
    this.#play('blindsOpen');
  }

  /**
   * Пускает звук хода створок вместо прежнего.
   *
   * Двух ходов разом у створок не бывает, поэтому и звуков разом не бывает: прежний уходит,
   * не обрываясь, см. ADR-50.
   */
  #play(name: SfxName): void {
    this.#hush(HUSH_SECONDS);
    this.#blinds = sfx(name);
  }

  /** Уводит звук створок за заданное время. Створки стоят — уводить нечего. */
  #hush(seconds: number): void {
    this.#blinds?.fadeOut(seconds);
    this.#blinds = null;
  }

  /** Снимает со створок всё, что игра на них показывала. */
  #clearFace(): void {
    this.#counting = null;
    this.#waiting = 0;
    this.#amount.view.visible = false;
    this.#button.hide();
  }

  /** Будит того, кто ждёт заглушку. Второй раз будить некого. */
  #wake(): void {
    const finished: (() => void) | null = this.#onFinished;

    this.#onFinished = null;
    finished?.();
  }

  /** Ставит надпись и ужимает её под створку: длинное имя фичи иначе вылезло бы за рамку. */
  #setCaption(text: string): void {
    this.#caption.text = text;
    fitLabel(this.#caption, { width: CAPTION_WIDTH, height: CAPTION_HEIGHT });
  }
}

/** Складывает узлы в держатель под слот скелета: сам слот Spine перезадаёт каждый кадр. */
function holder(...children: Container[]): Container {
  const node = new Container();
  node.addChild(...children);

  return node;
}
